import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase/config';
import {
  signInWithPopup, signInWithRedirect, getRedirectResult,
  OAuthProvider, signOut, onAuthStateChanged,
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, collection, getDocs, query, where
} from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const msProvider = new OAuthProvider('microsoft.com');
msProvider.setCustomParameters({ tenant: 'common' });

// Hitta företag och domänkonfig från Firestore
async function findDomainConfig(domain) {
  const companiesSnap = await getDocs(collection(db, 'companies'));
  for (const compDoc of companiesSnap.docs) {
    const data = compDoc.data();
    const allowedDomains = data.allowedDomains || [];
    const match = allowedDomains.find(d => d.domain === domain);
    if (match) {
      return {
        company: { id: compDoc.id, ...data },
        domainConfig: match
      };
    }
  }
  return null;
}

export async function provisionUser(firebaseUser) {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const userSnap = await getDoc(userRef);

  // Användaren finns redan – returnera direkt
  if (userSnap.exists()) {
    return { exists: true, data: userSnap.data() };
  }

  // Ny användare – kolla om ett importerat konto med samma email redan finns
  const email = firebaseUser.email || '';

  if (email) {
    const emailQ = await getDocs(
      query(collection(db, 'users'), where('email', '==', email))
    );
    if (!emailQ.empty) {
      // Hittade ett pre-importerat konto — migrera det till rätt UID-dokument
      const oldDoc = emailQ.docs[0];
      const oldData = oldDoc.data();
      const mergedData = {
        ...oldData,
        photoURL: firebaseUser.photoURL || oldData.photoURL || '',
        provisionedAt: new Date(),
        mergedFromId: oldDoc.id,
      };
      await setDoc(userRef, mergedData);
      // Ta bort det gamla dokumentet om det hade ett annat ID (t.ex. email som nyckel)
      if (oldDoc.id !== firebaseUser.uid) {
        await deleteDoc(oldDoc.ref);
      }
      return { exists: false, allowed: true, data: mergedData, isNew: false };
    }
  }

  const domain = email.split('@')[1]?.toLowerCase();

  if (!domain) {
    return {
      exists: false, allowed: false,
      reason: 'Ogiltig e-postadress.'
    };
  }

  // Kontrollera inbjudna marknadskonsulter (Google-inloggning)
  // Kollar först exakt e-post, sedan domännivå (@comdeva.se)
  let inviteSnap = await getDoc(doc(db, 'invitedConsultants', email.toLowerCase()));
  if (!inviteSnap.exists()) {
    inviteSnap = await getDoc(doc(db, 'invitedConsultants', `@${domain}`));
  }
  if (inviteSnap.exists()) {
    const invite = inviteSnap.data();
    const newUser = {
      name: firebaseUser.displayName || email.split('@')[0],
      email,
      role: invite.role || 'marknadskonsult',
      companyId: invite.companyId || '',
      departmentId: '',
      autoProvisioned: true,
      loginMethod: 'google',
      photoURL: firebaseUser.photoURL || '',
      provisionedAt: new Date(),
      createdAt: new Date(),
    };
    await setDoc(userRef, newUser);
    return { exists: false, allowed: true, data: newUser, isNew: true };
  }

  // Sök domänkonfig i companies-kollektionen (Microsoft + Google)
  const result = await findDomainConfig(domain);

  if (!result) {
    return {
      exists: false, allowed: false,
      reason: `E-postdomanen @${domain} ar inte behorig att anvanda LagbasN. Kontakta din administrator.`
    };
  }

  const { company, domainConfig } = result;

  // Använd specificerat affärsområde eller hitta första tillgängliga
  let departmentId = domainConfig.defaultDepartmentId || '';
  if (!departmentId && company.id) {
    const deptSnap = await getDocs(
      query(collection(db, 'departments'),
        where('companyId', '==', company.id))
    );
    if (!deptSnap.empty) {
      departmentId = deptSnap.docs[0].id;
    }
  }

  // Skapa användaren
  const newUser = {
    name: firebaseUser.displayName || email.split('@')[0],
    email,
    role: domainConfig.defaultRole || 'medarbetare',
    companyId: company.id,
    departmentId,
    autoProvisioned: true,
    photoURL: firebaseUser.photoURL || '',
    provisionedAt: new Date(),
    createdAt: new Date(),
  };

  await setDoc(userRef, newUser);

  return { exists: false, allowed: true, data: newUser, isNew: true };
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Kolla redirect-resultat vid sidladdning
    getRedirectResult(auth).then(async result => {
      if (result?.user) {
        await provisionUser(result.user);
      }
    }).catch(err => {
      console.error('Redirect result error:', err);
    });

    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      if (user) {
        // Hämta användarprofil
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          // Sync Google/Microsoft profile photo to Firestore so other users can see it
          if (user.photoURL && data.photoURL !== user.photoURL) {
            await updateDoc(doc(db, 'users', user.uid), { photoURL: user.photoURL });
            setUserProfile({ ...data, photoURL: user.photoURL });
          } else {
            setUserProfile(data);
          }
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function loginWithMicrosoft() {
    try {
      const result = await signInWithPopup(auth, msProvider);
      const provision = await provisionUser(result.user);

      if (!provision.allowed && !provision.exists) {
        // Domänen är inte godkänd – logga ut direkt
        await signOut(auth);
        return { success: false, error: provision.reason };
      }

      // Uppdatera userProfile direkt efter inloggning
      if (provision.data) {
        setUserProfile(provision.data);
      }

      return { success: true, isNew: provision.isNew };
    } catch (err) {
      if (err.code === 'auth/popup-blocked' ||
          err.code === 'auth/popup-closed-by-user' ||
          err.code === 'auth/cancelled-popup-request') {
        try {
          await signInWithRedirect(auth, msProvider);
          return { success: true };
        } catch (redirectErr) {
          console.error('Redirect login error:', redirectErr);
          return { success: false, error: redirectErr.message };
        }
      }
      console.error('Login error:', err);
      return { success: false, error: err.message };
    }
  }

  async function logout() {
    await signOut(auth);
    setUserProfile(null);
  }

  const value = {
    currentUser,
    userProfile,
    loginWithMicrosoft,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}